import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Modal from '../../Modal';
import { useApi } from '../../../lib/api';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function MilestoneForm({ onSave, onCancel, initial }) {
  const [title,     setTitle]     = useState(initial?.title     || '');
  const [startDate, setStartDate] = useState(initial?.start_date || '');
  const [endDate,   setEndDate]   = useState(initial?.end_date   || '');
  const [error,     setError]     = useState(null);
  const [saving,    setSaving]    = useState(false);

  async function handleSave() {
    if (!title.trim()) { setError('Title is required'); return; }
    if (startDate && !DATE_RE.test(startDate)) { setError('Invalid start date'); return; }
    if (endDate   && !DATE_RE.test(endDate))   { setError('Invalid end date');   return; }
    if (startDate && endDate && endDate < startDate) { setError('End date must be on or after start date'); return; }
    setSaving(true);
    try {
      await onSave({ title: title.trim(), start_date: startDate || null, end_date: endDate || null });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save');
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'rgba(6,182,212,0.06)',
      border: '1px solid rgba(6,182,212,0.2)',
      borderRadius: 10,
      padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <input
        type="text"
        className="form-input"
        placeholder="Milestone title *"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
        autoFocus
        style={{ margin: 0 }}
      />
      <div style={{ display: 'flex', gap: 10 }}>
        <input
          type="date"
          className="form-input"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          style={{ margin: 0, flex: 1 }}
        />
        <input
          type="date"
          className="form-input"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          style={{ margin: 0, flex: 1 }}
        />
      </div>
      {error && <div className="form-error" style={{ margin: 0 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn-secondary" style={{ padding: '4px 14px', fontSize: 13 }} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button className="btn-primary" style={{ padding: '4px 14px', fontSize: 13 }} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : (initial ? 'Save' : 'Add milestone')}
        </button>
      </div>
    </div>
  );
}

const TODAY = new Date().toISOString().substring(0, 10);

function isMilestoneOverdue(ms) {
  return !ms.is_completed && ms.end_date && ms.end_date < TODAY;
}

export default function ProjectDetailModal({ open, onClose, project, isOwner, onUpdate }) {
  const { appId }             = useParams();
  const api                   = useApi();
  const [milestones, setMilestones] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);
  const [adding,     setAdding]     = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [msFilter,   setMsFilter]   = useState('all');

  const load = useCallback(async () => {
    if (!project) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await api.listMilestones(appId, project.id);
      setMilestones(rows);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load milestones');
    } finally {
      setLoading(false);
    }
  }, [api, appId, project]);

  useEffect(() => {
    if (open && project) {
      setAdding(false);
      setEditingId(null);
      load();
    }
  }, [open, project, load]);

  async function handleAdd(body) {
    await api.createMilestone(appId, project.id, body);
    setAdding(false);
    await load();
    onUpdate?.();
  }

  async function handleEdit(mid, body) {
    await api.updateMilestone(appId, project.id, mid, body);
    setEditingId(null);
    await load();
    onUpdate?.();
  }

  async function handleToggle(ms) {
    setTogglingId(ms.id);
    try {
      await api.updateMilestone(appId, project.id, ms.id, { is_completed: ms.is_completed ? 0 : 1 });
      await load();
      onUpdate?.();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setTogglingId(null);
    }
  }

  async function handleDelete(mid) {
    setDeletingId(mid);
    try {
      await api.deleteMilestone(appId, project.id, mid);
      await load();
      onUpdate?.();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setDeletingId(null);
    }
  }

  if (!project) return null;

  const total     = milestones.length;
  const completed = milestones.filter((m) => m.is_completed).length;
  const overdueCnt = milestones.filter(isMilestoneOverdue).length;
  const pct       = total === 0 ? 0 : Math.round((completed / total) * 100);

  const MS_FILTERS = [
    { value: 'all',       label: 'All',       count: total },
    { value: 'pending',   label: 'Pending',   count: total - completed },
    { value: 'completed', label: 'Completed', count: completed },
    { value: 'overdue',   label: 'Overdue',   count: overdueCnt },
  ];

  const visibleMilestones = milestones.filter((ms) => {
    if (msFilter === 'completed') return !!ms.is_completed;
    if (msFilter === 'pending')   return !ms.is_completed;
    if (msFilter === 'overdue')   return isMilestoneOverdue(ms);
    return true;
  });

  return (
    <Modal
      open={open}
      title={`Milestones — ${project.name}`}
      onClose={onClose}
      size="lg"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Project summary */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: 10,
          padding: '12px 16px',
          display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: 'var(--fg-dim)',
        }}>
          {project.client_name && <span>🏢 {project.client_name}</span>}
          {project.owner_name  && <span>👤 Owner: {project.owner_name}</span>}
          {project.start_date  && <span>📅 {project.start_date} → {project.end_date || '…'}</span>}
          <span>📋 {project.completed_milestones ?? 0}/{project.total_milestones ?? 0} milestone{project.total_milestones !== 1 ? 's' : ''} ({project.completion_pct}% done)</span>
        </div>

        {/* Milestone progress */}
        {total > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, background: 'var(--bg-4)', borderRadius: 99, height: 6, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg,#06b6d499,#06b6d4)', borderRadius: 99, transition: 'width .4s' }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#06b6d4', minWidth: 60 }}>
              {completed}/{total} done
            </span>
          </div>
        )}

        {/* Error */}
        {error && <div className="banner banner-error">{error}</div>}

        {/* Loading */}
        {loading && <div className="tl-loading"><span className="tl-spinner" /> Loading milestones…</div>}

        {/* Milestone filter tabs */}
        {!loading && total > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {MS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setMsFilter(f.value)}
                style={{
                  padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                  border: `1px solid ${msFilter === f.value ? '#06b6d4' : 'rgba(255,255,255,0.1)'}`,
                  background: msFilter === f.value ? 'rgba(6,182,212,0.15)' : 'transparent',
                  color: msFilter === f.value ? '#06b6d4' : 'var(--fg-dim)',
                  cursor: 'pointer', transition: 'all .15s',
                }}
              >
                {f.label}
                <span style={{
                  marginLeft: 5, fontSize: 10, fontWeight: 700,
                  background: msFilter === f.value ? '#06b6d4' : 'rgba(255,255,255,0.08)',
                  color: msFilter === f.value ? '#000' : 'var(--fg-dim)',
                  padding: '1px 6px', borderRadius: 99,
                }}>
                  {f.count}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Milestones list */}
        {!loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {milestones.length === 0 && !adding && (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--fg-dim)', fontSize: 13 }}>
                No milestones yet.{isOwner ? ' Add the first one below.' : ''}
              </div>
            )}
            {milestones.length > 0 && visibleMilestones.length === 0 && !adding && (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--fg-dim)', fontSize: 13 }}>
                No {msFilter} milestones.
              </div>
            )}

            {visibleMilestones.map((ms) => {
              const isCompleted  = !!ms.is_completed;
              const isOverdue    = isMilestoneOverdue(ms);
              const isToggling   = togglingId === ms.id;
              const isDeleting   = deletingId === ms.id;
              const isEditingThis = editingId === ms.id;

              return (
                <div key={ms.id}>
                  {isEditingThis && isOwner ? (
                    <MilestoneForm
                      initial={ms}
                      onSave={(body) => handleEdit(ms.id, body)}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px',
                      background: isCompleted ? 'rgba(74,210,149,0.05)' : isOverdue ? 'rgba(248,113,113,0.04)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${isCompleted ? 'rgba(74,210,149,0.2)' : isOverdue ? 'rgba(248,113,113,0.18)' : 'rgba(255,255,255,0.07)'}`,
                      borderRadius: 9,
                      transition: 'background .15s',
                    }}>
                      {/* Toggle checkbox */}
                      <button
                        onClick={() => handleToggle(ms)}
                        disabled={isToggling}
                        title={isCompleted ? 'Mark incomplete' : 'Mark complete'}
                        style={{
                          width: 20, height: 20, borderRadius: '50%', flexShrink: 0, padding: 0,
                          border: `2px solid ${isCompleted ? '#4ad295' : 'var(--border-strong)'}`,
                          background: isCompleted ? '#4ad295' : 'transparent',
                          cursor: isToggling ? 'wait' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: isToggling ? 0.5 : 1, transition: 'all .2s',
                        }}
                      >
                        {isCompleted && (
                          <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
                            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </button>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 14, fontWeight: 600, color: 'var(--fg)',
                          textDecoration: isCompleted ? 'line-through' : 'none',
                          opacity: isCompleted ? 0.55 : 1,
                        }}>
                          {ms.title}
                        </div>
                        {(ms.start_date || ms.end_date) && (
                          <div style={{ fontSize: 11, color: 'var(--fg-dim)', marginTop: 3 }}>
                            {ms.start_date && <span>Start: {ms.start_date}</span>}
                            {ms.start_date && ms.end_date && <span> · </span>}
                            {ms.end_date   && <span>End: {ms.end_date}</span>}
                          </div>
                        )}
                      </div>

                      {/* Completed badge */}
                      {isCompleted && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                          background: 'rgba(74,210,149,0.12)', color: '#4ad295',
                          border: '1px solid rgba(74,210,149,0.25)',
                        }}>
                          Done
                        </span>
                      )}
                      {/* Overdue badge */}
                      {isOverdue && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                          background: 'rgba(248,113,113,0.12)', color: '#f87171',
                          border: '1px solid rgba(248,113,113,0.25)',
                        }}>
                          Overdue
                        </span>
                      )}

                      {/* Owner actions */}
                      {isOwner && (
                        <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                          <button
                            className="btn-secondary"
                            style={{ padding: '3px 10px', fontSize: 11 }}
                            onClick={() => { setEditingId(ms.id); setAdding(false); }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn-secondary"
                            style={{ padding: '3px 10px', fontSize: 11, color: '#f87171', borderColor: 'rgba(248,113,113,0.3)', opacity: isDeleting ? 0.5 : 1 }}
                            onClick={() => handleDelete(ms.id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? '…' : 'Delete'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add form */}
            {adding && isOwner && (
              <MilestoneForm
                onSave={handleAdd}
                onCancel={() => setAdding(false)}
              />
            )}
          </div>
        )}

        {/* Add button (owner only) */}
        {isOwner && !adding && !loading && (
          <button
            className="btn-secondary"
            style={{ alignSelf: 'flex-start', padding: '6px 16px', fontSize: 13 }}
            onClick={() => { setAdding(true); setEditingId(null); }}
          >
            + Add milestone
          </button>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose} style={{ padding: '6px 20px' }}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
